/**
 * 页面能力注册表(宿主页面侧,iframe 外部)。
 *
 * 供 liyu-pc-lib 的 AI 助手提供者(父页面)与 liyu-pc-base 页面组件(remotePagesV2 等)
 * 跨 bundle 共享:页面组件把自身实现的能力注册进来,AI 助手提供者按能力名查询并分发。
 *
 * 挂在 window 单例上(与 utils.ts 的 _GLOBAL_CACHE_KEY 同模式),避免 liyu-pc-base(dist)
 * 与 liyu-pc-lib(源码)两个模块图互相 import 造成的导出/版本问题。
 *
 * 同名能力支持多个页面实例同时注册:内部用栈管理,查询取栈顶(最后注册且未注销)的存活项。
 * keep-alive / 路由切换下,新页面注册自然覆盖旧页面,旧页面卸载时按引用注销自己。
 */
import Ajv, { type ErrorObject } from 'ajv';
import { Type, type TSchema } from 'typebox';

/** 全局注册表挂载 key(liyu-pc-lib 提供者与 liyu-pc-base 页面共用同一 key) */
export const PAGE_ABILITY_REGISTRY_KEY = '__LY_AI_PAGE_ABILITY__';

/** 能力执行结果统一约定:{ ok, data } | { ok: false, error } */
export interface PageAbilityResult {
  ok: boolean;
  data?: unknown;
  error?: string;
}

export type PageAbilityParameterSchema = TSchema;

export interface PageAbilityDefinition<TParams = unknown> {
  /** 宿主注册表能力名。page_ability_execute 默认用该字段调用。 */
  name: string;
  /** 业务工具名/展示名,便于大模型理解;未填时默认同 name */
  toolName?: string;
  /** 兼容旧能力名/旧工具名。仅用于执行匹配,不会影响主 name/toolName。 */
  aliases?: string[];
  label?: string;
  description: string;
  promptGuidelines?: string[];
  /** JSON Schema 风格参数描述,仅用于 page_ability_list 返回给模型 */
  parameters?: PageAbilityParameterSchema;
  /** 能力建议超时(毫秒),缺省 8000;写操作(如创建视图)建议 60000 */
  timeoutMs?: number;
  /** 是否写操作(便于模型/文档识别) */
  write?: boolean;
  execute: (params: TParams) => Promise<PageAbilityResult> | PageAbilityResult;
}

export type PageAbilityHandler<TParams = unknown> = PageAbilityDefinition<TParams>;
export type PageAbilityDescriptor = Omit<PageAbilityDefinition, 'execute'>;

interface RegistryState {
  stack: Map<string, PageAbilityHandler[]>;
  register(name: string, handler: PageAbilityHandler): () => void;
  get(name: string): PageAbilityHandler | null;
  find(nameOrToolName: string): PageAbilityHandler | null;
  list(): PageAbilityDescriptor[];
}

const ajv = new Ajv({
  allErrors: true,
  allowUnionTypes: true,
  strict: false,
});

function formatValidationErrors(errors: ErrorObject[] | null | undefined): string {
  if (!errors || errors.length === 0) {
    return '参数格式不符合要求';
  }
  return errors
    .map((error) => {
      if (error.keyword === 'required' && typeof error.params?.missingProperty === 'string') {
        return `缺少必填字段 ${error.params.missingProperty}`;
      }
      if (
        error.keyword === 'additionalProperties' &&
        typeof error.params?.additionalProperty === 'string'
      ) {
        return `存在未定义字段 ${error.params.additionalProperty}`;
      }
      const path = error.instancePath ? error.instancePath.replace(/\//g, '.').replace(/^\./, '') : '';
      const field = path || 'root';
      return `${field}: ${error.message || '参数不合法'}`;
    })
    .join('; ');
}

function normalizeParams(params: unknown): unknown {
  return params === undefined ? {} : params;
}

function normalizePageAbility(name: string, handler: PageAbilityHandler): PageAbilityHandler {
  const parameters = handler.parameters || Type.Object({});
  const validate = handler.parameters ? ajv.compile(handler.parameters) : null;
  return {
    ...handler,
    name,
    toolName: handler.toolName || name,
    label: handler.label || handler.toolName || name,
    description: handler.description || `${name} 页面能力`,
    parameters,
    execute: (params: unknown) => {
      const normalizedParams = normalizeParams(params);
      if (validate && !validate(normalizedParams)) {
        return {
          ok: false,
          error: `页面能力「${handler.toolName || name}」参数校验失败: ${formatValidationErrors(validate.errors)}`,
        };
      }
      return handler.execute(normalizedParams);
    },
  };
}

function ensureRegistry(): RegistryState {
  const g = globalThis as Record<string, unknown>;
  const existing = g[PAGE_ABILITY_REGISTRY_KEY] as RegistryState | undefined;
  if (
    existing &&
    typeof existing.get === 'function' &&
    typeof existing.find === 'function' &&
    typeof existing.list === 'function'
  ) {
    return existing;
  }
  const state: RegistryState = {
    stack: new Map(),
    register(name, handler) {
      const normalized = normalizePageAbility(name, handler);
      const list = state.stack.get(name) || [];
      list.push(normalized);
      state.stack.set(name, list);
      let alive = true;
      return () => {
        if (!alive) return;
        alive = false;
        const cur = state.stack.get(name) || [];
        const idx = cur.indexOf(normalized);
        if (idx > -1) {
          cur.splice(idx, 1);
          if (cur.length === 0) {
            state.stack.delete(name);
          } else {
            state.stack.set(name, cur);
          }
        }
      };
    },
    get(name) {
      const list = state.stack.get(name);
      return list && list.length > 0 ? list[list.length - 1] : null;
    },
    find(nameOrToolName) {
      const byName = state.get(nameOrToolName);
      if (byName) return byName;
      for (const list of state.stack.values()) {
        const current = list[list.length - 1];
        if (
          current &&
          (current.toolName === nameOrToolName || current.aliases?.includes(nameOrToolName))
        ) {
          return current;
        }
      }
      return null;
    },
    list() {
      return [...state.stack.values()]
        .map((list) => list[list.length - 1])
        .filter((item): item is PageAbilityHandler => !!item)
        .map(({ execute: _execute, ...definition }) => definition)
        .sort((a, b) => (a.toolName || a.name).localeCompare(b.toolName || b.name));
    },
  };
  g[PAGE_ABILITY_REGISTRY_KEY] = state;
  return state;
}

/** 注册一个页面能力,返回注销函数(组件卸载时调用) */
export function registerPageAbility(
  name: string,
  handler: PageAbilityHandler,
): () => void {
  return ensureRegistry().register(name, handler);
}

/** 按能力名查询当前存活(栈顶)的 handler */
export function getPageAbility(name: string): PageAbilityHandler | null {
  return ensureRegistry().get(name);
}

/** 按能力名或 toolName 查询当前存活(栈顶)的 handler */
export function findPageAbility(nameOrToolName: string): PageAbilityHandler | null {
  return ensureRegistry().find(nameOrToolName);
}

/** 列出当前已注册的全部页面能力定义(不含 execute) */
export function listPageAbilities(): PageAbilityDescriptor[] {
  return ensureRegistry().list();
}
